import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import useIsRestricted from '@hooks/useIsRestricted';
import { CreateCaseCallback, CustomSurvey } from 'vl-common/src/components/CustomSurvey';
import useUser from '@vl-core/hooks/useUser';
import { ConfirmationModal, errorModal } from 'vl-common/src/components/Modals';
import type { Survey } from 'vl-common/src/schemas/shared';

const CreateCase = ({
  open = false,
  setOpen,
  surveyJsDefinition,
  isLoadingSurveyJsDefinition,
  onSelfRefer
}: {
  open?: boolean;
  setOpen: (open: boolean) => void;
  surveyJsDefinition: any;
  isLoadingSurveyJsDefinition: boolean;
  onSelfRefer: (caseId: number) => void;
}) => {
  const dispatch = useDispatch();
  const user = useUser();
  const isRestricted = useIsRestricted();

  useEffect(() => {
    const newCaseModalRequested = !!new URLSearchParams(window.location.search).get('newcase');

    if (newCaseModalRequested) {
      setOpen(true);
    }
  }, []);

  const showErrorModal = (errorMessage: string) => {
    errorModal({
      title: 'Failed to create case',
      okText: 'Try again',
      content: (
        <div>
          <p style={{ margin: '20px 0' }}>{errorMessage}</p>
        </div>
      )
    });
  };

  const createCase: CreateCaseCallback = async ({ pat_guid, specialty, subSpecialty, notes, patient_sex }) => {
    let shouldDisableActionButton = true;
    const patientStoredSex = user.sex.charAt(0);
    await dispatch(
      actions.createCase({
        pat_guid,
        speccat: specialty,
        specarea: subSpecialty,
        notes,
        patient_sex: patient_sex || patientStoredSex
      }),
      false
    ).then(async (e) => {
      if (e?.case_id) {
        await dispatch(
          actions.acceptUserPolicy({
            user_guid: pat_guid,
            policy_code: 'CASE',
            accepted: true
          })
        ).then(() => {
          setOpen(false);
          onSelfRefer(e?.case_id);
        });
      } else {
        const err = (await e?.payload?.json()) ?? { message: e.statusText };
        shouldDisableActionButton = false;
        showErrorModal(err?.message);
      }

      if (patientStoredSex === 'N') {
        await dispatch(actions.userFetch());
      }
    });
    return shouldDisableActionButton;
  };
  const memoizedSurvey = useMemo(
    () => (
      <CustomSurvey
        bookAppointment={null}
        bookFaceToFace={null}
        modalVisible={false}
        createTask={null}
        dispatch={dispatch}
        getExpandedAnswers={null}
        getUsers={() => [user]}
        onData={null}
        pageType="patients"
        rawSurveyDefinition={surveyJsDefinition}
        isLoadingSurveyData={isLoadingSurveyJsDefinition}
        removeEmbeddedImages={null}
        routeBackToDashboard={() => {}}
        saveSurvey={null}
        saveSurveyToS3={null}
        setModalVisible={null}
        submitCaseTriage={null}
        surveyRes={{} as Survey}
        uploadEmbeddedImagesToS3={null}
        uploadFileToS3={null}
        user={user}
        userIsRestricted={isRestricted}
        // userNeedsRecentCardDetails={user?.needs_recent_card_details}
        updateUrgency={null}
        getCaseUrgency={() => null}
        createCase={createCase}
        shouldComplete={false}
        hasReadPermissions={!isRestricted}
        onDischarge={() => setOpen(false)}
      />
    ),
    [dispatch, surveyJsDefinition, isLoadingSurveyJsDefinition, user, isRestricted, setOpen]
  );

  return open ? (
    <ConfirmationModal footer={false} open={open} className="cancel-modal" onCancel={() => setOpen(false)} width={712}>
      {memoizedSurvey}
    </ConfirmationModal>
  ) : null;
};

export default CreateCase;
